
import React, { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileText, Upload, CheckCircle, AlertCircle, Loader2, Image, History, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import MarkdownEditor from "@/components/MarkdownEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ManageContent() {
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(null);
  const queryClient = useQueryClient();

  const { data: contentItems = [], isLoading } = useQuery({
    queryKey: ['content'],
    queryFn: () => base44.entities.Content.list(),
  });

  const teacherAnnouncements = contentItems.find(c => c.content_key === 'teacher_announcements') || {
    content_key: 'teacher_announcements',
    title: 'Teacher Announcements',
    content: '',
    file_type: 'text'
  };

  const studentAnnouncements = contentItems.find(c => c.content_key === 'student_announcements') || {
    content_key: 'student_announcements',
    title: 'Student Announcements',
    content: '',
    file_type: 'text'
  };

  const eaGuidelines = contentItems.find(c => c.content_key === 'ea_asd_guidelines') || {
    content_key: 'ea_asd_guidelines',
    title: 'EA/ASD Guidelines',
    file_url: '',
    file_type: 'pdf'
  };

  const [teacherText, setTeacherText] = useState(teacherAnnouncements.content || '');
  const [studentText, setStudentText] = useState(studentAnnouncements.content || '');

  // Update state when content is loaded from API
  useEffect(() => {
    setTeacherText(teacherAnnouncements.content || '');
    setStudentText(studentAnnouncements.content || '');
  }, [teacherAnnouncements.content, studentAnnouncements.content]);

  const saveContentMutation = useMutation({
    mutationFn: async ({ contentKey, data }) => {
      const existing = contentItems.find(c => c.content_key === contentKey);
      
      const payload = {
        ...data,
        content_key: contentKey,
        last_updated: new Date().toISOString()
      };

      if (existing) {
        await base44.entities.Content.update(existing.id, payload);
      } else {
        await base44.entities.Content.create(payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['content']);
      setMessage({ type: 'success', text: 'Content saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (error) => {
      setMessage({ type: 'error', text: error.message || 'Failed to save content' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const handleSaveTeacherAnnouncements = () => {
    saveContentMutation.mutate({
      contentKey: 'teacher_announcements',
      data: {
        title: 'Teacher Announcements',
        content: teacherText,
        file_type: 'text'
      }
    });
  };

  const handleSaveStudentAnnouncements = () => {
    saveContentMutation.mutate({
      contentKey: 'student_announcements',
      data: {
        title: 'Student Announcements',
        content: studentText,
        file_type: 'text'
      }
    });
  };

  const handleFileUpload = async (e, contentKey) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = file.type;
    const isPDF = fileType === 'application/pdf';
    const isImage = fileType.startsWith('image/');

    if (!isPDF && !isImage) {
      alert('Please upload a PDF or image file');
      return;
    }

    try {
      setUploading(contentKey);
      
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      
      await saveContentMutation.mutateAsync({
        contentKey: contentKey,
        data: {
          title: 'EA/ASD Guidelines',
          file_url: file_url,
          file_type: isPDF ? 'pdf' : 'image'
        }
      });
      
      setUploading(null);
    } catch (error) {
      setUploading(null);
      setMessage({ type: 'error', text: error.message || 'Failed to upload file' });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const restoreMutation = useMutation({
    mutationFn: async ({ contentKey, index }) => {
      const res = await axios.post(
        `${BACKEND_URL}/api/content/${contentKey}/restore`,
        { index },
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['content']);
      setMessage({ type: 'success', text: 'Previous version restored.' });
      setTimeout(() => setMessage(null), 4000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to restore' });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const handleRestore = (contentKey, index, item) => {
    if (confirm(`Restore this earlier version (replaced on ${format(new Date(item.replaced_at), 'MMM d, yyyy h:mm a')})?\n\nThe current file will be moved into history.`)) {
      restoreMutation.mutate({ contentKey, index });
    }
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-red-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Manage Content</h1>
            <p className="text-slate-600 mt-1">Update Announcements and Guidelines</p>
          </div>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Teacher Announcements</CardTitle>
            {teacherAnnouncements.last_updated && (
              <p className="text-xs text-slate-500">Last updated: {format(new Date(teacherAnnouncements.last_updated), 'MMM d, yyyy h:mm a')}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <MarkdownEditor
              value={teacherText}
              onChange={setTeacherText}
              placeholder="Enter announcements for teachers. Use the toolbar for bold, links, lists..."
              minHeight="10rem"
            />
            <Button 
              onClick={handleSaveTeacherAnnouncements}
              className="bg-red-700 hover:bg-red-800"
              disabled={saveContentMutation.isPending}
            >
              {saveContentMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Teacher Announcements
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Student Announcements</CardTitle>
            {studentAnnouncements.last_updated && (
              <p className="text-xs text-slate-500">Last updated: {format(new Date(studentAnnouncements.last_updated), 'MMM d, yyyy h:mm a')}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <MarkdownEditor
              value={studentText}
              onChange={setStudentText}
              placeholder="Enter announcements for students. Use the toolbar for bold, links, lists..."
              minHeight="10rem"
            />
            <Button 
              onClick={handleSaveStudentAnnouncements}
              className="bg-red-700 hover:bg-red-800"
              disabled={saveContentMutation.isPending}
            >
              {saveContentMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Student Announcements
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>EA/ASD Guidelines (Student View)</CardTitle>
            {eaGuidelines.last_updated && (
              <p className="text-xs text-slate-500">Last updated: {format(new Date(eaGuidelines.last_updated), 'MMM d, yyyy h:mm a')}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Upload PDF or Image</Label>
              <Input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => handleFileUpload(e, 'ea_asd_guidelines')}
                disabled={uploading === 'ea_asd_guidelines'}
              />
              <p className="text-xs text-slate-500 mt-1">Upload a PDF document or image file with EA/ASD guidelines for students</p>
            </div>

            {uploading === 'ea_asd_guidelines' && (
              <Alert className="bg-blue-50 border-blue-200">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <AlertDescription className="text-blue-900">
                  Uploading file...
                </AlertDescription>
              </Alert>
            )}

            {eaGuidelines.file_url && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {eaGuidelines.file_type === 'pdf' ? (
                      <FileText className="w-8 h-8 text-red-600" />
                    ) : (
                      <Image className="w-8 h-8 text-blue-600" />
                    )}
                    <div>
                      <p className="font-medium text-slate-900">Current Guidelines File</p>
                      <p className="text-xs text-slate-500">
                        Type: {eaGuidelines.file_type === 'pdf' ? 'PDF Document' : 'Image'}
                      </p>
                    </div>
                  </div>
                  <a 
                    href={eaGuidelines.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View File →
                  </a>
                </div>
              </div>
            )}

            {eaGuidelines.history?.length > 0 && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <History className="w-4 h-4 text-slate-600" />
                  <p className="text-sm font-semibold text-slate-700">Previous versions</p>
                  <span className="text-xs text-slate-500">
                    (last {eaGuidelines.history.length} kept)
                  </span>
                </div>
                <ul className="space-y-2">
                  {eaGuidelines.history.map((h, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white border border-slate-200"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {h.file_type === 'pdf' ? (
                          <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                        ) : (
                          <Image className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm text-slate-800 truncate">
                            Version replaced on {h.replaced_at ? format(new Date(h.replaced_at), 'MMM d, yyyy h:mm a') : 'earlier'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <a
                          href={h.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1"
                        >
                          View
                        </a>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRestore('ea_asd_guidelines', i, h)}
                          disabled={restoreMutation.isPending}
                          className="h-7 text-xs"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Restore
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
